/**
 * Government & Utility Payments — single-screen UX (P-B.8.0).
 *
 * Design contract:
 *   - User picks a Provider (STC / SEC / ZATCA / …).
 *   - User picks a Mode via TAB (not a toggle — clearer for accountants):
 *       • "Register Obligation (Bill)" → creates unpaid AP entry.
 *       • "Record Direct Payment"      → creates paid entry against a bank.
 *   - Provider template pre-fills VAT rate and the reference-field label
 *     (Meter Number for SEC, GOSI Establishment ID for GOSI, …).
 *   - Everything flows through the standard `supplier_bills` pipeline so
 *     it appears in VAT Return + Reconciliation with zero manual work.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { bankService } from '../services/bankService';
import { toast } from 'sonner';
import {
  Building2, CheckCircle2, Landmark, Loader2, Phone, Receipt, RefreshCw,
  Scale, Truck, Users, Wifi, Zap, Droplets, Mail, Bell, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import GovUtilityBillDetailModal from '../components/GovUtilityBillDetailModal';


const ICONS = {
  phone: Phone, wifi: Wifi, zap: Zap, droplets: Droplets, mail: Mail,
  landmark: Landmark, scale: Scale, users: Users, truck: Truck,
  receipt: Receipt, building: Building2,
};

const fmtSAR = (n) => `${Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} SAR`;


export default function GovUtilityPayments() {
  // Providers registry
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState('');
  const provider = useMemo(
    () => providers.find((p) => p.id === providerId),
    [providers, providerId],
  );

  // Mode: obligation | direct_payment
  const [mode, setMode] = useState('obligation');

  // Form fields
  const [referenceValue, setReferenceValue] = useState('');
  const [billDate, setBillDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [dueDate, setDueDate] = useState('');
  const [subtotal, setSubtotal] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [vatMode, setVatMode] = useState('inclusive');
  const [description, setDescription] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  // Direct-payment specific
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [bankId, setBankId] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [chequeNo, setChequeNo] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [banks, setBanks] = useState([]);

  // Recurring block (schema-ready · scheduler wave later)
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('monthly');
  const [recurringNextDue, setRecurringNextDue] = useState('');
  // Reminder-day offsets. UI presets are 30 / 14 / 7 days.
  const [reminderDays, setReminderDays] = useState([]);

  // ── Backfill (Wave P-B.8.2) ──────────────────────────────────────
  // Two-step operator flow: (1) preview counts, (2) execute if
  // acceptable. Never writes on load; preview must be explicitly
  // clicked. Execute is idempotent so repeat clicks are harmless.
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult,  setBackfillResult]  = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);

  // ── Production Data Migration (Wave P-B.8.5) ─────────────────────
  // One-click cleanup of legacy data on tenants that pre-date the
  // EIM prefix / amount_remaining fix / bank mirror. Same Preview →
  // Confirm → Execute contract as the GL backfill above.
  const [migPreview,  setMigPreview]  = useState(null);
  const [migLoading,  setMigLoading]  = useState(false);
  const [migResult,   setMigResult]   = useState(null);
  const [migRunning,  setMigRunning]  = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [monthFilter, setMonthFilter] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [repairRunning, setRepairRunning] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  // Two-state model for the bank list — distinguishes "empty" (success
  // with no rows) from "failed" (network / auth). The dropdown uses
  // this to show the correct helper text so users don't burn cycles
  // wondering whether they need to add an account or file a ticket.
  const [banksLoadFailed, setBanksLoadFailed] = useState(false);

  const loadProviders = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/gov-utility-payments/providers');
      setProviders(data.providers || []);
    } catch (err) {
      toast.error('Failed to load providers');
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '1000' });
      if (monthFilter) params.append('month', monthFilter);
      const { data } = await axiosInstance.get(`/gov-utility-payments/recent?${params}`);
      setRecent(data.items || []);
    } catch { /* non-fatal */ }
  }, [monthFilter]);

  const loadBanks = useCallback(async () => {
    // Delegated to bankService — see /src/services/bankService.js for
    // the canonical endpoint. Distinguishing "load failed" from
    // "success but empty" is required so the dropdown can show the
    // right helper text (data missing vs. system unreachable).
    const { ok, accounts, error } = await bankService.listAccounts();
    if (!ok) {
      setBanksLoadFailed(true);
      setBanks([]);
      // Non-blocking toast — the field is only required in Direct
      // Payment mode and submit-time validation already refuses to
      // proceed when no bank is picked.
      toast.error('Unable to load bank accounts. Please retry.');
      // Surface the underlying error to the console for support
      // engineers; users see only the friendly toast.
      if (error) console.warn('[bankService] load failed', error);
      return;
    }
    setBanksLoadFailed(false);
    setBanks(accounts);
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/projects');
      setProjects(data || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadProviders(); loadBanks(); loadProjects(); },
    [loadProviders, loadBanks, loadProjects]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // When provider changes, seed VAT rate from its template.
  useEffect(() => {
    if (provider && vatRate === '') {
      setVatRate(String(provider.default_vat_rate));
    }
  }, [provider, vatRate]);

  // Root-cause fix (Mobily 99.99 → 114.99): default to VAT-INCLUSIVE
  // for VAT-rated providers so the entered amount == what you pay.
  useEffect(() => {
    if (provider) {
      setVatMode(Number(provider.default_vat_rate) > 0 ? 'inclusive' : 'none');
    }
  }, [provider]);

  const { baseAmt, computedVat, total } = useMemo(() => {
    const s = parseFloat(subtotal) || 0;
    const r = parseFloat(vatRate) || 0;
    if (vatMode === 'none' || r <= 0) {
      return { baseAmt: s, computedVat: 0, total: s };
    }
    if (vatMode === 'inclusive') {
      const base = Math.round((s / (1 + r / 100)) * 100) / 100;
      return {
        baseAmt: base,
        computedVat: Math.round((s - base) * 100) / 100,
        total: s,
      };
    }
    const vat = Math.round(s * r) / 100;
    return { baseAmt: s, computedVat: vat, total: Math.round((s + vat) * 100) / 100 };
  }, [subtotal, vatRate, vatMode]);

  const resetForm = () => {
    setReferenceValue('');
    setSubtotal('');
    setDescription('');
    setProjectId('');
    setPaymentReference('');
    setPaymentMethod('bank_transfer');
    setChequeNo('');
    setBeneficiaryName('');
    setDueDate('');
    setRecurringEnabled(false);
    setRecurringFrequency('monthly');
    setRecurringNextDue('');
    setReminderDays([]);
  };

  const toggleReminderDay = (d) => {
    setReminderDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
    );
  };

  // ── Backfill actions ────────────────────────────────────────────
  // Preview is read-only on the server; safe to call anytime.
  const runBackfillPreview = async () => {
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const { data } = await axiosInstance.get(
        '/gov-utility-payments/backfill/preview');
      setBackfillPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setBackfillLoading(false);
    }
  };

  // Executes the same pipeline new bills use. Idempotent — repeat
  // clicks post nothing extra. Preview is refreshed at the end so
  // the operator can see counters drop to zero on success.
  const runBackfillExecute = async () => {
    if (!backfillPreview) return;
    // Explicit confirmation — the write is idempotent but still a
    // large-fanout operation, and there is no undo for a posted
    // journal (only reverse-journal via Approvals).
    const ok = window.confirm(
      `Post ${backfillPreview.estimated_journals} journal(s) through the standard `
      + 'Accounting Engine? This is idempotent — nothing extra is posted for '
      + 'bills that already carry a journal.',
    );
    if (!ok) return;
    setBackfillRunning(true);
    try {
      const { data } = await axiosInstance.post(
        '/gov-utility-payments/backfill/execute');
      setBackfillResult(data);
      toast.success(
        `Backfill done · ${data.bill_journals} bill + ${data.payment_journals} payment journals`,
      );
      await runBackfillPreview();
      loadRecent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillRunning(false);
    }
  };

  // ── Data Migration actions (Wave P-B.8.5) ──────────────────────
  const runMigPreview = async () => {
    setMigLoading(true);
    setMigResult(null);
    try {
      const { data } = await axiosInstance.get(
        '/gov-utility-payments/production-migration/preview');
      setMigPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview migration');
    } finally {
      setMigLoading(false);
    }
  };

  const runMigExecute = async () => {
    if (!migPreview) return;
    const ok = window.confirm(
      `Apply ${migPreview.total_operations} data-migration operations? `
      + 'This will rename GOV-* bills to EIM-*, fix stale amount_remaining, '
      + 'and backfill bank-ledger mirror rows. Idempotent — safe to re-run.',
    );
    if (!ok) return;
    setMigRunning(true);
    try {
      const { data } = await axiosInstance.post(
        '/gov-utility-payments/production-migration/execute');
      setMigResult(data);
      const changed = (data.rename_gov_to_eim || 0)
                     + (data.fix_amount_remaining || 0)
                     + (data.fix_null_remaining   || 0)
                     + (data.bank_mirror_backfill || 0);
      toast.success(`Migration done · ${changed} record(s) updated`);
      await runMigPreview();
      loadRecent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setMigRunning(false);
    }
  };

  // Feb 2026 · Owner directive — one-click auto-classify for every
  // legacy Gov/Utility bill (real supplier + default account 52000).
  const [autoClassLoading, setAutoClassLoading] = useState(false);
  const [autoClassResult,  setAutoClassResult]  = useState(null);
  const runAutoClassifyLegacy = async () => {
    const ok = window.confirm(
      'إصلاح جميع الفواتير القديمة تلقائياً:\n'
      + ' • استبدال supplier_id الوهمي بمورد حقيقي\n'
      + ' • تعيين حساب 52000 (Administrative Expenses) لكل فاتورة بلا حساب\n\n'
      + 'إعادة التنفيذ آمنة (idempotent). متابعة؟');
    if (!ok) return;
    setAutoClassLoading(true);
    setAutoClassResult(null);
    try {
      const { data } = await axiosInstance.post(
        '/gov-utility-payments/auto-classify-legacy');
      setAutoClassResult(data);
      toast.success(
        `أُصلح ${data.supplier_repaired} مورد · ${data.account_defaulted} حساب`);
      loadRecent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-classify failed');
    } finally {
      setAutoClassLoading(false);
    }
  };

  const submit = async () => {
    if (!providerId) return toast.error('Please pick a provider');
    if (!referenceValue) return toast.error(`Please enter ${provider?.reference_label_en}`);
    if (!subtotal || parseFloat(subtotal) <= 0)
      return toast.error('Enter a positive subtotal');
    if (mode === 'direct_payment' && !bankId)
      return toast.error('Please pick the bank account you paid from');
    if (recurringEnabled && !recurringNextDue)
      return toast.error('Pick the first upcoming due date for the recurring schedule');

    setSubmitting(true);
    try {
      const payload = {
        provider_id:     providerId,
        mode,
        reference_value: referenceValue,
        bill_date:       billDate,
        subtotal:        parseFloat(subtotal),
        vat_rate:        parseFloat(vatRate) || 0,
        vat_mode:        vatMode,
        description:     description || null,
        due_date:        dueDate || null,
        recurring_enabled:   recurringEnabled,
        recurring_frequency: recurringEnabled ? recurringFrequency : null,
        recurring_next_due:  recurringEnabled ? recurringNextDue  : null,
        reminder_days:       recurringEnabled ? reminderDays      : null,
        project_id:          projectId || null,
      };
      if (mode === 'direct_payment') {
        payload.payment_date      = paymentDate;
        payload.paid_from_bank_id = bankId;
        payload.payment_reference = paymentReference || null;
        payload.payment_method = paymentMethod || null;
        payload.cheque_no = paymentMethod === 'check' ? (chequeNo || null) : null;
        payload.beneficiary_name = beneficiaryName || null;
      }
      const { data } = await axiosInstance.post('/gov-utility-payments', payload);
      toast.success(
        `${data.bill.bill_number} · ${fmtSAR(data.bill.total)} · ${
          mode === 'obligation' ? 'Bill created' : 'Payment recorded'
        }${data.bill.gov_utility?.recurring?.enabled ? ' · Recurring' : ''}`,
      );
      resetForm();
      loadRecent();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  // Group providers by category for the picker
  const grouped = useMemo(() => {
    const g = {};
    providers.forEach((p) => {
      const cat = p.category_en || 'Other';
      (g[cat] = g[cat] || []).push(p);
    });
    return g;
  }, [providers]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="gov-utility-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Landmark className="text-indigo-600" size={26} />
          Government &amp; Utility Payments
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Single entry point for STC / SEC / NWC / ZATCA / GOSI / Customs / Municipality bills.
          Behind the scenes, each entry lands in the standard AP + VAT pipeline.
        </p>
      </div>

      {/* Provider picker */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Pick the provider</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">
                {cat}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {list.map((p) => {
                  const Icon = ICONS[p.icon] || Receipt;
                  const active = providerId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setProviderId(p.id)}
                      data-testid={`provider-${p.id}`}
                      className={`flex items-center gap-2 border rounded-lg p-3 text-sm text-start transition ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-900 shadow-sm'
                          : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                      }`}
                    >
                      <Icon size={16} className={active ? 'text-indigo-600' : 'text-slate-500'} />
                      <span className="font-medium">{p.display_name_en}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Entry form — appears once a provider is chosen */}
      {provider && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt size={16} className="text-indigo-600" />
              New entry — {provider.display_name_en}
              <span className="text-[11px] text-slate-500 font-normal ml-2">
                (Intent: {provider.intent})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList>
                <TabsTrigger value="obligation" data-testid="mode-obligation">
                  Register Obligation (Bill)
                </TabsTrigger>
                <TabsTrigger value="direct_payment" data-testid="mode-direct">
                  Record Direct Payment
                </TabsTrigger>
              </TabsList>
              <TabsContent value="obligation" className="text-xs text-slate-500 pt-2">
                Creates an unpaid supplier bill (goes into AP). Pay it later via the standard payment flow.
              </TabsContent>
              <TabsContent value="direct_payment" className="text-xs text-slate-500 pt-2">
                Creates a bill that is already paid from a bank account. AP stays flat; VAT is still claimed.
              </TabsContent>
            </Tabs>

            {/* Reference field (provider-specific label) */}
            <div>
              <Label>{provider.reference_label_en}</Label>
              <Input
                value={referenceValue}
                onChange={(e) => setReferenceValue(e.target.value)}
                placeholder={provider.reference_label_en}
                data-testid="input-reference"
                dir="ltr"
              />
              {provider.reference_pattern && (
                <p className="text-[11px] text-slate-400 mt-1">
                  Expected pattern: <code>{provider.reference_pattern}</code>
                </p>
              )}
            </div>

            {/* Amount grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label>Bill Date</Label>
                <Input type="date" value={billDate}
                        onChange={(e) => setBillDate(e.target.value)}
                        data-testid="input-bill-date" />
              </div>
              <div>
                <Label>{vatMode === 'inclusive' ? 'Amount — VAT inclusive (SAR)' : 'Amount (SAR)'}</Label>
                <Input type="number" step="0.01" min="0" value={subtotal}
                        onChange={(e) => setSubtotal(e.target.value)}
                        data-testid="input-subtotal" />
              </div>
              <div>
                <Label>VAT Rate (%)</Label>
                <Input type="number" step="0.01" min="0" value={vatRate}
                        onChange={(e) => setVatRate(e.target.value)}
                        data-testid="input-vat-rate" />
              </div>
              <div>
                <Label>Total</Label>
                <div className="border rounded px-3 py-2 text-sm bg-slate-50 h-10 flex items-center font-semibold tabular-nums">
                  {fmtSAR(total)}
                </div>
              </div>
            </div>

            {/* VAT mode — root-cause fix: entered amount is what you PAY */}
            <div className="space-y-1">
              <Label>معالجة الضريبة · VAT Treatment</Label>
              <div className="flex flex-wrap gap-2" data-testid="vat-mode-selector">
                {[
                  { v: 'inclusive', ar: 'المبلغ شامل الضريبة', en: 'Inclusive' },
                  { v: 'addon',     ar: 'إضافة الضريبة فوق المبلغ', en: 'Add-on' },
                  { v: 'none',      ar: 'بدون ضريبة', en: 'No VAT' },
                ].map((o) => (
                  <button key={o.v} type="button"
                    onClick={() => setVatMode(o.v)}
                    data-testid={`vat-mode-${o.v}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      vatMode === o.v
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
                    }`}>
                    {o.ar} · {o.en}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">
                {vatMode === 'inclusive'
                  ? 'المبلغ المدخل هو الإجمالي الذي تدفعه — يُستخرج الأساس والضريبة تلقائياً.'
                  : vatMode === 'addon'
                    ? 'تنبيه: ستُضاف الضريبة فوق المبلغ المدخل — الإجمالي سيكون أعلى مما أدخلت.'
                    : 'لن تُسجَّل أي ضريبة على هذه الفاتورة ولن تدخل في إقرار الضريبة.'}
              </p>
            </div>

            {mode === 'obligation' && (
              <div>
                <Label>Due Date <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input type="date" value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        data-testid="input-due-date" />
              </div>
            )}

            {mode === 'direct_payment' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Payment Date</Label>
                  <Input type="date" value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          data-testid="input-payment-date" />
                </div>
                <div>
                  <Label>Paid from Bank</Label>
                  <Select value={bankId} onValueChange={setBankId}>
                    <SelectTrigger data-testid="select-bank">
                      <SelectValue placeholder="Pick a bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {banksLoadFailed ? (
                        <div className="px-3 py-2 text-xs text-rose-600" data-testid="bank-load-failed">
                          Unable to load bank accounts. Please retry.
                        </div>
                      ) : banks.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-500" data-testid="bank-empty">
                          No active bank accounts. Add one from Banking &amp; Treasury first.
                        </div>
                      ) : (
                        banks.map((b) => (
                          <SelectItem key={b.id} value={b.id} data-testid={`bank-opt-${b.id}`}>
                            {bankService.formatLabel(b)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Reference <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          placeholder="SADAD confirmation / bank ref"
                          data-testid="input-payment-ref"
                          dir="ltr" />
                </div>
                <div>
                  <Label>طريقة الدفع / Payment Method</Label>
                  <select
                    data-testid="gov-payment-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="bank_transfer">تحويل بنكي / Bank Transfer</option>
                    <option value="check">شيك / Cheque</option>
                  </select>
                </div>
                {paymentMethod === 'check' && (
                  <div>
                    <Label>رقم الشيك / Cheque No *</Label>
                    <Input value={chequeNo}
                            onChange={(e) => setChequeNo(e.target.value)}
                            placeholder="123456"
                            data-testid="gov-cheque-no"
                            dir="ltr" />
                  </div>
                )}
                <div>
                  <Label>اسم المستفيد <span className="text-slate-400 text-xs">(اختياري — عامل / جهة)</span></Label>
                  <Input value={beneficiaryName}
                          onChange={(e) => setBeneficiaryName(e.target.value)}
                          placeholder="مثال: العامل محمد أحمد"
                          data-testid="gov-beneficiary-name"
                          dir="rtl" />
                </div>
              </div>
            )}

            <div>
              <Label>Description <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Textarea rows={2} value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          data-testid="input-description" />
            </div>

            {/* Project linkage — expense enters project cost (51xxx) */}
            <div>
              <Label>
                المشروع / Project{' '}
                <span className="text-slate-400 text-xs">(اختياري — عند اختياره تدخل التكلفة في تكلفة المشروع)</span>
              </Label>
              <select
                data-testid="gov-project-select"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">بدون مشروع — مصروف إداري عام</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {projectId && (
                <p className="text-[11px] text-indigo-700 mt-1" dir="rtl">
                  🏗 سيُرحَّل المبلغ (بدون الضريبة) إلى «تكلفة المشاريع 51» موسوماً بالمشروع ويظهر في ربحيته.
                </p>
              )}
            </div>

            {/* ── Recurring block ─────────────────────────────────────
                Owner directive 2026-07-05: schema-ready today; the
                scheduler wave later will use these fields to auto-
                generate the next bill and fire reminders. No
                background job runs against these yet — we simply
                capture the user's intent.
            */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <RefreshCw size={16} className="text-indigo-600" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      This is a recurring bill
                    </div>
                    <div className="text-[11px] text-slate-500">
                      We&apos;ll remember the schedule &amp; reminders.
                      Auto-generation runs in a later release — nothing is scheduled yet.
                    </div>
                  </div>
                </div>
                <Switch
                  checked={recurringEnabled}
                  onCheckedChange={setRecurringEnabled}
                  data-testid="switch-recurring"
                />
              </div>

              {recurringEnabled && (
                <div className="p-4 space-y-4" data-testid="recurring-panel">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">
                      Frequency
                    </Label>
                    <RadioGroup
                      value={recurringFrequency}
                      onValueChange={setRecurringFrequency}
                      className="flex flex-wrap gap-4 mt-2"
                      data-testid="radio-frequency"
                    >
                      {[
                        { v: 'monthly',   l: 'Monthly'   },
                        { v: 'quarterly', l: 'Quarterly' },
                        { v: 'yearly',    l: 'Yearly'    },
                      ].map((o) => (
                        <label
                          key={o.v}
                          htmlFor={`freq-${o.v}`}
                          className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer transition ${
                            recurringFrequency === o.v
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                              : 'border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          <RadioGroupItem
                            value={o.v}
                            id={`freq-${o.v}`}
                            data-testid={`freq-${o.v}`}
                          />
                          {o.l}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>First upcoming due</Label>
                      <Input
                        type="date"
                        value={recurringNextDue}
                        onChange={(e) => setRecurringNextDue(e.target.value)}
                        data-testid="input-next-due"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-1">
                        <Bell size={12} /> Remind me before due
                      </Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[30, 14, 7, 3, 1].map((d) => (
                          <label
                            key={d}
                            htmlFor={`reminder-${d}`}
                            className={`flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-xs cursor-pointer transition ${
                              reminderDays.includes(d)
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                : 'border-slate-200 hover:border-slate-400 text-slate-600'
                            }`}
                          >
                            <Checkbox
                              id={`reminder-${d}`}
                              checked={reminderDays.includes(d)}
                              onCheckedChange={() => toggleReminderDay(d)}
                              data-testid={`reminder-${d}`}
                            />
                            {d} day{d === 1 ? '' : 's'}
                          </label>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-2">
                        Reminders are stored today; the notifier wave will surface them in the alerts bell.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Live preview */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700 space-y-1">
              <div className="flex justify-between">
                <span>Base (net of VAT)</span>
                <span className="tabular-nums" data-testid="preview-base">{fmtSAR(baseAmt)}</span>
              </div>
              <div className="flex justify-between">
                <span>VAT ({vatMode === 'none' ? 0 : vatRate || 0}%)</span>
                <span className="tabular-nums" data-testid="preview-vat">{fmtSAR(computedVat)}</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-300 pt-1 mt-1">
                <span>Total (what you pay)</span>
                <span className="tabular-nums" data-testid="preview-total">{fmtSAR(total)}</span>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="btn-submit"
                className="min-w-[180px]"
              >
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : (mode === 'obligation'
                      ? 'Create Bill'
                      : 'Record Payment')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent entries */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">
              All entries / جميع الفواتير
              <span className="text-xs font-normal text-slate-500 mr-2 ml-2" data-testid="gu-entries-count">({recent.length})</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">بحث حسب الشهر:</label>
              <input
                type="month"
                data-testid="gu-month-filter"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 text-sm"
              />
              {monthFilter && (
                <button
                  data-testid="gu-month-clear"
                  onClick={() => setMonthFilter('')}
                  className="text-xs text-slate-500 hover:text-red-600 border border-slate-200 rounded px-2 py-1">
                  ✕ مسح
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {recent.length === 0 ? (
            <p className="text-sm text-slate-500">No entries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <th className="text-left  py-2 pr-3">Bill #</th>
                    <th className="text-left  py-2 pr-3">Date</th>
                    <th className="text-left  py-2 pr-3">Provider</th>
                    <th className="text-left  py-2 pr-3">Reference</th>
                    <th className="text-left  py-2 pr-3">Project / المشروع</th>
                    <th className="text-right py-2 pr-3">Base</th>
                    <th className="text-right py-2 pr-3">VAT</th>
                    <th className="text-right py-2 pr-3">Total</th>
                    <th className="text-left  py-2 pr-3">Journal</th>
                    <th className="text-center py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id}
                        className="border-b border-slate-100 last:border-0
                                   hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedBill(r)}
                        data-testid={`recent-row-${r.bill_number}`}
                        title="Click to view details and lifecycle actions">
                      <td className="py-2 pr-3 font-medium">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{r.bill_number}</span>
                          {r.gov_utility?.recurring?.enabled && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-full"
                              title={`Recurring · ${r.gov_utility.recurring.frequency} · next ${r.gov_utility.recurring.next_due || '—'}`}
                              data-testid="badge-recurring"
                            >
                              <RefreshCw size={9} />
                              {r.gov_utility.recurring.frequency}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3">{r.bill_date}</td>
                      <td className="py-2 pr-3">{r.supplier_name}</td>
                      <td className="py-2 pr-3 text-slate-600 text-xs" dir="ltr">
                        {r.gov_utility?.reference_value || '—'}
                      </td>
                      <td className="py-2 pr-3 text-xs" data-testid={`project-cell-${r.bill_number}`}>
                        {r.project_name
                          ? <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full">{r.project_name}</span>
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtSAR(r.subtotal)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtSAR(r.vat_amount)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums font-semibold">{fmtSAR(r.total)}</td>
                      <td className="py-2 pr-3 text-xs" dir="ltr">
                        {r.journal_no ? (
                          <span
                            className="inline-flex items-center gap-1 text-emerald-700"
                            data-testid={`journal-${r.bill_number}`}
                          >
                            <ShieldCheck size={11} />
                            <span className="font-mono">{r.journal_no}</span>
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-amber-600"
                            title="No GL journal yet — run Backfill"
                            data-testid={`no-journal-${r.bill_number}`}
                          >
                            <AlertTriangle size={11} /> not posted
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-center">
                        {r.status === 'paid' ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                            <CheckCircle2 size={12} /> Paid
                          </span>
                        ) : (
                          <span className="inline-block text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                            Open (AP)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Backfill · Full GL Integration (Wave P-B.8.2) ────────────
          Legacy Gov/Utility bills created before this wave sit in
          supplier_bills without a journal. Preview shows the counts;
          Execute replays them through the SAME Accounting Engine that
          new bills use — no parallel journal writer, no shortcuts.
       */}
      <Card data-testid="backfill-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck size={16} className="text-indigo-600" />
            GL Integration · Backfill
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <p className="text-xs text-slate-500">
            Replays legacy Government / Utility bills through the standard
            Accounting Engine to produce their Journal, Payment, and Bank
            Ledger entries. Runs the SAME pipeline used by new bills — no
            shortcuts. Idempotent: a bill that already carries a journal is
            skipped automatically.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={runBackfillPreview}
              disabled={backfillLoading}
              data-testid="btn-backfill-preview"
            >
              {backfillLoading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : 'Preview'}
            </Button>
            <Button
              onClick={runBackfillExecute}
              disabled={
                !backfillPreview
                || backfillRunning
                || backfillPreview.estimated_journals === 0
              }
              data-testid="btn-backfill-execute"
            >
              {backfillRunning
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (backfillPreview && backfillPreview.estimated_journals === 0
                    ? 'Nothing to backfill'
                    : `Execute (${backfillPreview?.estimated_journals ?? 0} journals)`)}
            </Button>
          </div>

          {backfillPreview && (
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs"
              data-testid="backfill-preview"
            >
              {[
                ['Bills Found',       backfillPreview.bills_found],
                ['Already Posted',    backfillPreview.already_posted],
                ['Need Bill Post',    backfillPreview.need_bill_post],
                ['Need Payment',      backfillPreview.need_payment],
                ['Est. Journals',     backfillPreview.estimated_journals],
                ['Est. Time (s)',     backfillPreview.eta_seconds],
              ].map(([label, val]) => (
                <div key={label}
                     className="border border-slate-200 rounded p-3 bg-slate-50">
                  <div className="uppercase tracking-wider text-slate-500 text-[10px] mb-1">
                    {label}
                  </div>
                  <div className="text-lg font-semibold text-slate-900 tabular-nums">
                    {val}
                  </div>
                </div>
              ))}
            </div>
          )}

          {backfillResult && (
            <div
              className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-800"
              data-testid="backfill-result"
            >
              <div className="font-semibold mb-1">Backfill complete</div>
              Processed: <b>{backfillResult.processed}</b>
              {' · '}Bill journals: <b>{backfillResult.bill_journals}</b>
              {' · '}Payment journals: <b>{backfillResult.payment_journals}</b>
              {' · '}Skipped (bill): <b>{backfillResult.skipped_bill}</b>
              {' · '}Skipped (payment): <b>{backfillResult.skipped_payment}</b>
              {backfillResult.errors.length > 0 && (
                <div className="mt-2 text-rose-700">
                  <b>{backfillResult.errors.length} error(s):</b>
                  <ul className="list-disc pl-5 mt-1">
                    {backfillResult.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>{e.bill_number} — {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Data Migration (Wave P-B.8.5) ──────────────────────────
          One-click cleanup for tenants that pre-date recent waves:
            • GOV-* bill numbers → EIM-* rename
            • status='paid' but amount_remaining>0 → zero it
            • amount_remaining=null → recompute
            • paid bills with a bank_account but no bank_transactions
              mirror row → generate one via the shared
              ``mirror_cash_movement`` helper
          Idempotent — safe to click multiple times.
       */}
      <Card data-testid="migration-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw size={16} className="text-emerald-600" />
            Data Migration · Legacy Cleanup
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <p className="text-xs text-slate-500">
            Renames legacy <code className="text-[10px] bg-slate-100 px-1 rounded">GOV-*</code> bills to
            <code className="text-[10px] bg-slate-100 px-1 rounded"> EIM-*</code>,
            fixes stale <code className="text-[10px] bg-slate-100 px-1 rounded">amount_remaining</code>,
            and backfills missing bank-ledger mirror rows. Idempotent — the same button in a
            month will show <code className="text-[10px] bg-slate-100 px-1 rounded">0</code> operations if nothing drifted.
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={runMigPreview}
                    disabled={migLoading} data-testid="btn-mig-preview">
              {migLoading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : 'Preview'}
            </Button>
            <Button onClick={runMigExecute}
                    disabled={!migPreview || migRunning
                              || migPreview.total_operations === 0}
                    data-testid="btn-mig-execute">
              {migRunning
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (migPreview && migPreview.total_operations === 0
                    ? 'Nothing to migrate'
                    : `Execute (${migPreview?.total_operations ?? 0} ops)`)}
            </Button>
          </div>

          {migPreview && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-xs"
                 data-testid="mig-preview">
              {[
                ['Rename GOV → EIM',    migPreview.rename_gov_to_eim],
                ['Fix Paid Balances',   migPreview.fix_amount_remaining],
                ['Fix Null Remaining',  migPreview.fix_null_remaining],
                ['Bank Mirror Backfill',migPreview.bank_mirror_backfill],
                ['Total Ops',           migPreview.total_operations],
              ].map(([label, val]) => (
                <div key={label} className="border border-slate-200 rounded p-3 bg-slate-50">
                  <div className="uppercase tracking-wider text-slate-500 text-[10px] mb-1">
                    {label}
                  </div>
                  <div className="text-lg font-semibold text-slate-900 tabular-nums">
                    {val}
                  </div>
                </div>
              ))}
            </div>
          )}

          {migResult && (
            <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-800"
                 data-testid="mig-result">
              <div className="font-semibold mb-1">Migration complete</div>
              Renamed: <b>{migResult.rename_gov_to_eim}</b>
              {' · '}Paid-balances fixed: <b>{migResult.fix_amount_remaining}</b>
              {' · '}Null-remaining fixed: <b>{migResult.fix_null_remaining}</b>
              {' · '}Bank mirrors added: <b>{migResult.bank_mirror_backfill}</b>
              {migResult.errors?.length > 0 && (
                <div className="mt-2 text-rose-700">
                  <b>{migResult.errors.length} error(s):</b>
                  <ul className="list-disc pl-5 mt-1">
                    {migResult.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>{e.bill_number} — {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Auto-Classify Legacy (Feb 2026) ─────────────────────────
          Owner directive: every existing Gov/Utility bill must (a) be
          linked to a REAL supplier record and (b) default to
          52000 · Administrative Expenses. Idempotent · safe to
          re-run.                                                    */}
      <Card data-testid="auto-classify-legacy-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw size={16} className="text-indigo-600" />
            🚀 Auto-Classify Legacy Bills · تصنيف تلقائي للفواتير القديمة
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            كل فاتورة Gov/Utility قديمة تُصلَح بضغطة واحدة: مورد حقيقي +
            حساب <b>52000</b> (Administrative Expenses) عند غيابه.
            <b> Idempotent</b> — آمنة لإعادة التنفيذ.
          </p>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          <Button size="sm"
                  onClick={runAutoClassifyLegacy}
                  disabled={autoClassLoading}
                  data-testid="btn-auto-classify-legacy">
            {autoClassLoading ? 'جاري الإصلاح…' : 'شغّل الآن'}
          </Button>
          {autoClassResult && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs font-mono"
                 data-testid="auto-classify-result">
              <div>Scanned:  <b>{autoClassResult.scanned}</b></div>
              <div>Supplier repaired: <b className="text-emerald-700">{autoClassResult.supplier_repaired}</b></div>
              <div>Account defaulted: <b className="text-emerald-700">{autoClassResult.account_defaulted}</b></div>
              {autoClassResult.errors?.length > 0 && (
                <div className="text-rose-700 mt-1">
                  Errors: {autoClassResult.errors.length}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                Admin account: {autoClassResult.admin_account?.account_number} · {autoClassResult.admin_account?.account_name}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Repair Voided Payments (P-B.8.7) ────────────────────────
          One-click sweeper: cancels active payments whose parent
          gov/utility bill was Voided in a prior session (before this
          fix landed). Fixes "Missing Journal" variance in Cash-vs-
          Payments reconciliation.                                        */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw size={16} className="text-rose-600" />
            Repair Voided Payments
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <p className="text-xs text-slate-500">
            Voiding a bill created before <b>2026-07-03</b> left its child
            Payment row alive, causing a false <i>Missing Journal</i>
            variance in Cash-vs-Payments reconciliation. This button
            sweeps those orphans and marks them <code className="text-[10px] bg-slate-100 px-1 rounded">status=cancelled</code>.
            Idempotent — safe to run repeatedly.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={async () => {
                setRepairRunning(true);
                setRepairResult(null);
                try {
                  const r = await axiosInstance.post(
                    '/gov-utility-payments/repair/cancel-orphan-payments');
                  setRepairResult(r.data);
                  toast.success(
                    r.data.cancelled_payments > 0
                      ? `تم إلغاء ${r.data.cancelled_payments} دفعة يتيمة`
                      : 'لا توجد دفعات يتيمة — كل شيء نظيف');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Repair failed');
                } finally {
                  setRepairRunning(false);
                }
              }}
              disabled={repairRunning}
              data-testid="btn-repair-orphan-payments">
              {repairRunning
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : 'Sweep Orphan Payments'}
            </Button>
          </div>
          {repairResult && (
            <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-800"
                 data-testid="repair-result">
              Scanned <b>{repairResult.scanned_bills}</b> voided bill(s) ·
              Cancelled <b>{repairResult.cancelled_payments}</b> orphan payment(s).
              {repairResult.details?.length > 0 && (
                <ul className="list-disc pl-5 mt-2">
                  {repairResult.details.slice(0, 10).map((d, i) => (
                    <li key={i}>
                      {d.bill_number} → {d.payment_number}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Bill Detail + Lifecycle Modal (P-B.8.6) ──────────────────
          Clicking a row in "Recent entries" opens this dialog. It
          shows the bill summary, GL journal number, bank ledger link,
          and — if the bill is not already in a terminal state — the
          Void / Reversing-Entry actions wired to the generic Document
          Lifecycle Service.                                              */}
      <GovUtilityBillDetailModal
        open={!!selectedBill}
        bill={selectedBill}
        onClose={() => setSelectedBill(null)}
        onChanged={loadRecent}
      />
    </div>
  );
}
